import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { TextArea } from '../../text-fields'
import { Tag } from '../../misc'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const TagsContainer = styled.div`
  position: relative;
  display: flex;
`

const TagWrapper = styled.div<{ selected: boolean; selectable: boolean; focused: boolean }>`
  background-color: #fff;
  position: relative;
  padding: 6px 8px 7px;
  top: 1px;
  border: 1px solid transparent;
  border-bottom: none;

  &:only-child {
    ${props =>
      !props.selectable &&
      css`
        padding: 6px 0 7px;
      `}
  }

  &:first-child {
    ${props =>
      !props.selectable &&
      css`
        padding: 6px 8px 7px 0;
      `}
  }

  ${props => {
    const borderColor = props.focused ? props.theme.color.primary600 : props.theme.color.onSurfaceLowEmphasys
    return (
      props.selected &&
      props.selectable &&
      css`
        z-index: 1;
        border: 1px solid ${borderColor};
        border-bottom: none;
        border-radius: 8px 8px 0 0;
      `
    )
  }}
  ${props => {
    return (
      props.selectable &&
      css`
        cursor: pointer;
      `
    )
  }}
`

const TextAreaStyled = styled(TextArea)`
  width: 100%;
  border-radius: 0 0 8px 8px;
  min-width: 450px;

  ${props => props.theme.media.mobile`
    min-width: 100%;
  `}
`

const TagStyled = styled(Tag)<{ selected: boolean }>`
  background-color: ${props => (props.selected ? props.theme.color.primary50 : props.theme.color.backgroundSecondary)};
  > svg {
    fill: ${props => (props.selected ? props.theme.color.primary100 : props.theme.color.onSurfaceLowEmphasys)};
  }
`

type Item = {
  id: string
  title: string
  editable?: boolean
  selectable?: boolean
  placeholder?: string
  value?: string
  readonly?: boolean
}

export const TextAreaWithTags: React.FC<{
  items: Item[]
  selected?: string
  onTextChange(id: string, text: string): void
  onSelectionChange(id: string): void
  onRemove(id: string): void
}> = ({ items, selected, onTextChange, onSelectionChange, onRemove }) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      selected && onTextChange(selected, e.target.value)
    },
    [onTextChange, selected],
  )

  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [focused, setFocused] = useState(false)

  const handleSelectionChange = useCallback(
    (id: string) => {
      onSelectionChange(id)
    },
    [onSelectionChange],
  )

  const selectedItem = useMemo(() => items.find(item => item.id === selected), [items, selected])
  const hasEditable = useMemo(() => items.some(item => item.editable !== false), [items])
  const hasReadonly = useMemo(() => items.some(item => item.readonly !== false), [items])
  const isFocusable = !selectedItem?.readonly

  return (
    <Wrapper>
      <TagsContainer data-test-id={'tags-container'}>
        {[...items]
          .sort(item => (item.editable !== false ? -1 : 1))
          .map(item => {
            const isSelected = item.id === selected && typeof selected !== 'undefined'
            return (
              <TagWrapper
                key={item.id}
                selected={isSelected}
                selectable={item.selectable !== false}
                focused={focused}
                onClick={() => item.selectable !== false && handleSelectionChange(item.id)}
              >
                <TagStyled selected={isSelected} title={item.title} index={item.id} onRemove={onRemove} />
              </TagWrapper>
            )
          })}
      </TagsContainer>
      {hasEditable || hasReadonly ? (
        <TextAreaStyled
          value={selectedItem?.value || ''}
          ref={textAreaRef}
          name={'Promocode text'}
          onChange={handleChange}
          placeholder={selectedItem?.placeholder}
          onFocus={() => isFocusable && setFocused(true)}
          onBlur={() => isFocusable && setFocused(false)}
          readonly={selectedItem?.readonly}
        />
      ) : null}
    </Wrapper>
  )
}
